// Compact JSON projections of the Song AST, sized for the model's
// context window. Full subtrees are fetched on demand via get_block.

import type {
  Effect,
  Expression,
  PatternSlot,
  Song,
  Statement,
} from '../blocks/types';

export type SongSummary = {
  statements: StatementSummary[];
};

type StatementSummary =
  | { id: string; kind: 'setcpm' | 'setcps'; value: string }
  | {
      id: string;
      kind: 'const';
      name: string;
      expr: ExpressionSummary;
      comment?: string;
    }
  | {
      id: string;
      kind: 'expression';
      expr: ExpressionSummary;
      comment?: string;
    }
  | { id: string; kind: 'samples'; source: string }
  | { id: string; kind: 'comment'; content: string };

type ExpressionSummary =
  | {
      id: string;
      kind: 'voice.s' | 'voice.note';
      pattern: string;
      effectCount: number;
      effectSummary: string;
    }
  | { id: string; kind: 'stack'; childCount: number; childIds: string[] }
  | { id: string; kind: 'arrange'; entryCount: number; entryIds: string[] }
  | {
      id: string;
      kind: 'ref';
      name: string;
      effectCount: number;
      effectSummary: string;
    }
  | { id: string; kind: 'customFunction'; preview: string };

export function summarizeSong(song: Song): SongSummary {
  return {
    statements: song.statements.map(summarizeStatement),
  };
}

function summarizeStatement(stmt: Statement): StatementSummary {
  switch (stmt.kind) {
    case 'setcpm':
    case 'setcps':
      return { id: stmt.id, kind: stmt.kind, value: numericPreview(stmt.value) };
    case 'const':
      return {
        id: stmt.id,
        kind: 'const',
        name: stmt.name,
        expr: summarizeExpression(stmt.expr),
        comment: stmt.leadingComment,
      };
    case 'expression':
      return {
        id: stmt.id,
        kind: 'expression',
        expr: summarizeExpression(stmt.expr),
        comment: stmt.leadingComment,
      };
    case 'samples':
      return { id: stmt.id, kind: 'samples', source: stmt.source };
    case 'comment':
      return { id: stmt.id, kind: 'comment', content: stmt.content };
  }
}

function summarizeExpression(expr: Expression): ExpressionSummary {
  switch (expr.kind) {
    case 'voice.s':
    case 'voice.note':
      return {
        id: expr.id,
        kind: expr.kind,
        pattern: patternPreview(expr.pattern),
        effectCount: expr.effects.length,
        effectSummary: effectsPreview(expr.effects),
      };
    case 'stack':
      return {
        id: expr.id,
        kind: 'stack',
        childCount: expr.children.length,
        childIds: expr.children.map((c) => c.id),
      };
    case 'arrange':
      return {
        id: expr.id,
        kind: 'arrange',
        entryCount: expr.entries.length,
        entryIds: expr.entries.map((e) => e.id),
      };
    case 'ref':
      return {
        id: expr.id,
        kind: 'ref',
        name: expr.name,
        effectCount: expr.effects?.length ?? 0,
        effectSummary: effectsPreview(expr.effects ?? []),
      };
    case 'customFunction':
      return {
        id: expr.id,
        kind: 'customFunction',
        preview: truncate(expr.source.replace(/\s+/g, ' '), 80),
      };
  }
}

function patternPreview(slot: PatternSlot): string {
  if (slot.kind === 'raw') return slot.source;
  return slot.nodes.map(miniPreview).join(' ');
}

function miniPreview(node: unknown): string {
  const n = node as { kind: string; name?: string; children?: unknown[]; inner?: unknown };
  if (n.kind === 'atom') return n.name ?? '?';
  if (n.kind === 'rest') return '~';
  if (n.kind === 'group' && n.children) {
    return `[${n.children.map(miniPreview).join(' ')}]`;
  }
  if (n.kind === 'parallel' && n.children) {
    return `[${n.children.map(miniPreview).join(', ')}]`;
  }
  if (n.kind === 'choice' && n.children) {
    return `<${n.children.map(miniPreview).join(' ')}>`;
  }
  return `«${n.kind}»`;
}

function effectsPreview(effects: Effect[]): string {
  if (effects.length === 0) return '';
  return effects.map((e) => `.${e.kind}(…)`).join('');
}

function numericPreview(n: unknown): string {
  const v = n as { kind: string; value?: number; source?: string; name?: string };
  if (v.kind === 'num') return String(v.value);
  if (v.kind === 'ref') return v.name ?? '?';
  if (v.kind === 'raw') return v.source ?? '?';
  return `«${v.kind}»`;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
